import React, { useId, useMemo, useState } from "react";
import { CCCheckbox, CCSpinner } from "@pymblecitizencoders/cc-ui";
import {
	Armchair,
	Car,
	CircleAlert,
	CircleDot,
	ClipboardList,
	ExternalLink,
	LucideIcon,
	Megaphone,
	NotebookPen,
	Plus,
	Projector,
	ScanLine,
	Shield,
	Signpost,
	Utensils,
} from "lucide-react";
import {
	EventRequirementModal,
	RequirementDetails,
} from "./EventRequirementModal";
import { useEventRequirements } from "../../hooks/useEventRequirements";
import { needsDetails } from "../../utils/requirementForms";
import { Tooltip } from "../common/Tooltip";
import {
	EventDoc,
	EventRequirement,
	EventRequirementRequest,
	EventRequirementRequestSummary,
	EventRequirementSelection,
} from "../../types/event";
import "./eventManager.css";

interface EventRequirementsTabProps {
	event: EventDoc;
	readOnly: boolean;
	onChange: (patch: Partial<EventDoc>) => void;
}

/**
 * Dictionary glyph names to the components, so the catalogue stays data.
 *
 * The map stays in the client on purpose: the dictionary can name an icon, but
 * it cannot ship one. A requirement added to the dictionary with a glyph this
 * build has never heard of still renders — with the fallback below — rather
 * than leaving a hole in the card.
 */
const ICONS: Record<string, LucideIcon> = {
	utensils: Utensils,
	megaphone: Megaphone,
	shield: Shield,
	signpost: Signpost,
	car: Car,
	projector: Projector,
	armchair: Armchair,
	"scan-line": ScanLine,
};

/** Something rather than nothing when the dictionary names an unknown glyph. */
const FALLBACK_ICON = CircleDot;

/** Ditto for colour: the dictionary's swatch, or a neutral tile. */
const FALLBACK_COLOUR = "var(--em-navy)";

/**
 * The tone of a ticket status, so "Resolved" reads as done and "Pending" as
 * waiting. Freshservice's own four; anything else stays neutral.
 */
const STATUS_TONES: Record<string, string> = {
	open: "open",
	pending: "pending",
	resolved: "done",
	closed: "done",
};

/** "27 Sept, 2:12 pm" in school time, or null for a missing or bad date. */
const formatUpdated = (iso?: string | null) => {
	if (!iso) return null;
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return null;
	return d.toLocaleString("en-AU", {
		day: "numeric",
		month: "short",
		hour: "numeric",
		minute: "2-digit",
		timeZone: "Australia/Sydney",
	});
};

/**
 * The summary worth showing, or null.
 *
 * Only a `ready` summary with text counts; anything else (still generating,
 * failed) is treated as none rather than shown as a placeholder, because the
 * ticket link is already there for anyone who wants the detail.
 */
const readySummary = (summary?: EventRequirementRequestSummary | null) =>
	summary?.State === "ready" && summary.Text?.trim() ? summary : null;

/**
 * The body of the summary tooltip, under its "AI Summary" heading: the summary
 * itself, the next step when the team is waiting on the organiser, then how
 * much has been said and when.
 */
const SummaryDetails: React.FC<{
	summary: EventRequirementRequestSummary;
}> = ({ summary }) => {
	const nextStep = summary.NextStep?.trim();
	const updated = formatUpdated(summary.SourceUpdatedAt);
	const replies = summary.Replies ?? 0;
	const meta = [
		replies > 0 ? `${replies} ${replies === 1 ? "reply" : "replies"}` : null,
		updated ? `updated ${updated}` : null,
	].filter(Boolean);
	return (
		<>
			<span>{summary.Text?.trim()}</span>
			{summary.AwaitingOrganiser && nextStep && (
				<span className="em-req-summary__action">
					<CircleAlert size={13} aria-hidden="true" />
					<span>
						<span className="em-req-summary__action-label">Over to you: </span>
						{nextStep}
					</span>
				</span>
			)}
			{meta.length > 0 && (
				<span className="em-req-summary__meta">{meta.join(" · ")}</span>
			)}
		</>
	);
};

/** Whether a ticket has been raised for this requirement. */
const isSubmitted = (stored?: EventRequirementSelection) =>
	stored?.Request?.Id != null;

/**
 * Where the requirement's request stands, under its description.
 *
 * A ticket is a link, and the link stops the click reaching the card's label
 * for the same reason the note button does: following it must not untick the
 * requirement. The status is the ticket's own when the API supplies one and
 * "Raised" until then — see `EventRequirementRequest.Status`.
 *
 * A summary, when there is one, shows in the shared Tooltip on hovering the
 * box. That tooltip is portalled onto body, which matters here: the tab sits in
 * a scroll container, and a popover positioned inside the card was clipped by
 * it and put a scrollbar on the tab. Screen readers get the same text from a
 * visually-hidden span the ticket link is described by, since the tooltip only
 * exists while hovered. The "waiting on you" marker stays visible without
 * hovering: that is the one part of the summary nobody should have to go
 * looking for.
 */
const RequestStatus: React.FC<{
	request?: EventRequirementRequest | null;
	error?: { Reason?: string } | null;
}> = ({ request, error }) => {
	const summaryId = useId();
	if (request?.Id != null) {
		const status = request.Status?.trim() || "Raised";
		const tone = STATUS_TONES[status.toLowerCase()] ?? "open";
		const number = request.Ref || request.Id;
		const summary = readySummary(request.Summary);
		const describedBy = summary ? summaryId : undefined;
		const awaiting = !!summary?.AwaitingOrganiser && !!summary.NextStep?.trim();
		const boxClass = `em-req-request em-req-ticket${summary ? " has-summary" : ""}`;
		const contents = (
			<>
				{/* The ticket number alone: which service desk holds it is the
				    team's business, not the organiser's. The accessible name still
				    says it is a ticket. */}
				{request.Url ? (
					<a
						href={request.Url}
						target="_blank"
						rel="noopener noreferrer"
						className="em-req-request__link"
						aria-label={`Ticket ${number} (opens in a new tab)`}
						aria-describedby={describedBy}
						onClick={(e) => e.stopPropagation()}
					>
						#{number}
						<ExternalLink size={11} aria-hidden="true" />
					</a>
				) : (
					<span
						className="em-req-request__link"
						aria-label={`Ticket ${number}`}
					>
						#{number}
					</span>
				)}
				<span className="em-req-ticket__status">
					{/* Named, because a bordered pill on its own reads as a button. */}
					<span className="em-req-request__label">Status</span>
					<span className={`em-req-status em-req-status--${tone}`}>
						{status}
					</span>
					{awaiting && (
						<span
							className="em-req-awaiting"
							title="The team is waiting on you"
							aria-label="The team is waiting on you"
							role="img"
						>
							<CircleAlert size={14} aria-hidden="true" />
						</span>
					)}
				</span>
				{summary && (
					<span id={summaryId} className="em-sr-only">
						{summary.Text?.trim()}
						{awaiting && ` Over to you: ${summary.NextStep?.trim()}`}
					</span>
				)}
			</>
		);
		return summary ? (
			<Tooltip
				label="AI Summary"
				details={<SummaryDetails summary={summary} />}
				align="start"
				interactive
				className={boxClass}
			>
				{contents}
			</Tooltip>
		) : (
			<span className={boxClass}>{contents}</span>
		);
	}
	if (error?.Reason) {
		return (
			<span className="em-req-request em-req-ticket" title={error.Reason}>
				<span className="em-req-ticket__status">
					<span className="em-req-request__label">Status</span>
					<span className="em-req-status em-req-status--failed">Not sent</span>
				</span>
				<span className="em-muted">{error.Reason}</span>
			</span>
		);
	}
	return null;
};

/**
 * Requirements tab — what the event needs arranging, ticked from a catalogue.
 *
 * The catalogue is fixed rather than free text because these get handed to the
 * teams who provide them — catering, grounds, IT — and a list they can filter
 * beats a sentence someone has to read.
 *
 * It comes from the shared Dictionary collection rather than a constant in this
 * file, so the school can add a ninth requirement without a release. That means
 * this tab has to cope with the list being slow, failing, or not seeded at all,
 * which is why there are three states before the grid.
 *
 * The event stores an array of objects; this tab works in ticked ids and a note
 * map, which is the shape the grid and the modal actually want. The conversion
 * happens at both edges here and nowhere else.
 */
export const EventRequirementsTab: React.FC<EventRequirementsTabProps> = ({
	event,
	readOnly,
	onChange,
}) => {
	const { requirements, requirementsLoading, requirementsError } =
		useEventRequirements();
	/** The stored array, read as ticked ids and notes by id. */
	const stored = useMemo<EventRequirementSelection[]>(
		() => event.Requirements ?? [],
		[event.Requirements],
	);
	const selected = useMemo(() => stored.map((r) => r.Id), [stored]);
	const notes = useMemo(
		() =>
			Object.fromEntries(
				stored.filter((r) => r.Note).map((r) => [r.Id, String(r.Note)]),
			),
		[stored],
	);
	const answersById = useMemo(
		() => Object.fromEntries(stored.map((r) => [r.Id, r.Answers ?? []])),
		[stored],
	);
	const storedById = useMemo(
		() => Object.fromEntries(stored.map((r) => [r.Id, r])),
		[stored],
	);
	/** Which requirement's note is open. Null is closed. */
	const [noting, setNoting] = useState<EventRequirement | null>(null);

	/**
	 * Tick and untick.
	 *
	 * Ticking opens the note straight away — see EventRequirementModal for
	 * why the moment of ticking is when someone actually has the detail in mind.
	 * Unticking takes the note with it: a note against a requirement the event
	 * no longer has is not information, and leaving it would quietly resurrect
	 * itself if the box were ever ticked again.
	 */
	const toggle = (req: EventRequirement) => {
		const on = selected.includes(req.id);
		// A requirement with a ticket raised stays ticked: the team is already
		// working it, and unticking would drop the only record on the event
		// that it was ever sent. Cancelling is done with the team, not here.
		if (on && isSubmitted(storedById[req.id])) return;
		onChange({
			Requirements: on
				? stored.filter((r) => r.Id !== req.id)
				: // Existing entries are passed through untouched, so the AddedAt
					// and AddedBy the API stamped on them survive this edit.
					[...stored, { Id: req.id }],
		});
		setNoting(on ? null : req);
	};

	const saveDetails = ({ note, answers }: RequirementDetails) => {
		if (!noting) return;
		const id = noting.id;
		const hasForm = noting.fields.length > 0;
		onChange({
			Requirements: stored.map((r) => {
				if (r.Id !== id) return r;
				// A requirement with a form stores answers and leaves the note
				// alone, so a note written before the form existed is not lost.
				if (hasForm) return { ...r, Answers: answers };
				// An emptied box is a removal — null rather than "", which is what
				// the API stores for a requirement with nothing written against it.
				return { ...r, Note: note || null };
			}),
		});
		setNoting(null);
	};

	return (
		<div>
			<div className="em-req-head">
				<div>
					<h6 className="em-section-heading">Select your event requirements</h6>
					<p className="em-muted">
						Customise your event by choosing what you need for it.
					</p>
				</div>
				{/* The count is the thing people check before leaving the tab, so
				    it sits opposite the heading rather than under the grid. */}
				<span className="em-req-count">
					Selected requirements: <strong>{selected.length}</strong>
				</span>
			</div>

			{requirementsLoading ? (
				<div className="em-text-center em-mt-sm">
					<CCSpinner size="sm" label="Loading requirements..." />
				</div>
			) : requirementsError ? (
				<div className="em-picker-empty">
					The requirements list could not be loaded. Anything already ticked on
					this event is unaffected.
				</div>
			) : requirements.length === 0 ? (
				/* The catalogue is a dictionary entry, and it has not been seeded
				   yet. Say which one — the person reading this can have it added,
				   and an empty grid on its own looks like a broken tab. */
				<div className="em-picker-empty">
					No requirements have been set up. Ask for the
					&ldquo;EventRequirements&rdquo; dictionary to be populated.
				</div>
			) : (
				<div className="em-req-grid">
					{requirements.map((req) => {
						const Icon = ICONS[req.icon] ?? FALLBACK_ICON;
						const on = selected.includes(req.id);
						const locked = on && isSubmitted(storedById[req.id]);
						const note = notes[req.id];
						const hasForm = req.fields.length > 0;
						const answered = (answersById[req.id] ?? []).length > 0;
						/* Only a ticked card can owe answers; an unticked one is
						   not being asked for. */
						const missing =
							on && hasForm && needsDetails(req.fields, answersById[req.id]);
						return (
							<label
								key={req.id}
								className={`em-req-card${on ? " is-on" : ""}${missing ? " is-wide-badge" : ""}${locked ? " is-locked" : ""}`}
							>
								<CCCheckbox
									id={`em-req-${req.id}`}
									label=""
									checked={on}
									disabled={readOnly || locked}
									onChange={() => toggle(req)}
								/>
								<span
									className="em-req-icon"
									style={{
										backgroundColor: req.colour || FALLBACK_COLOUR,
									}}
									aria-hidden="true"
								>
									<Icon size={20} />
								</span>
								<span className="em-req-body">
									<span className="em-req-title">{req.label}</span>
									<span className="em-muted">{req.description}</span>
								</span>
								{/* Outside the body, so the ticket spans the whole card
								    rather than the column beside the tick and icon. */}
								{on && (
									<RequestStatus
										request={storedById[req.id]?.Request}
										error={storedById[req.id]?.RequestError}
									/>
								)}
								{/* On selected cards only. Eight permanent "add note"
								    buttons would be furniture nobody asked for, but a
								    ticked card needs a way back into its note —
								    clicking the card itself unticks it.

								    A real button, and it stops the click reaching the
								    label: without that, editing a note would untick the
								    requirement it belongs to. */}
								{on && !readOnly && !hasForm && (
									<button
										type="button"
										className={`em-req-note${note ? "" : " em-req-note--add"}`}
										title={note || "Add a note"}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											setNoting(req);
										}}
									>
										{note ? (
											<NotebookPen size={13} aria-hidden="true" />
										) : (
											<Plus size={13} aria-hidden="true" />
										)}
										Note
									</button>
								)}
								{/* A requirement with a form. "Details needed" until
								    its required questions are SAVED: closing the modal
								    is allowed, and this is how that stays visible
								    rather than reaching the team as a blank ticket. */}
								{on && !readOnly && hasForm && (
									<button
										type="button"
										className={`em-req-note${missing ? " em-req-note--needed" : ""}`}
										title={
											missing
												? "Some required details have not been filled in"
												: "Edit details"
										}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											setNoting(req);
										}}
									>
										{missing ? (
											<CircleAlert size={13} aria-hidden="true" />
										) : (
											<ClipboardList size={13} aria-hidden="true" />
										)}
										{missing ? "Details needed" : "Details"}
									</button>
								)}
								{/* Read-only still shows that a note exists, just not a
								    control that pretends to open it. */}
								{on && readOnly && !hasForm && note && (
									<span className="em-req-note" title={note}>
										<NotebookPen size={13} aria-hidden="true" />
										Note
									</span>
								)}
								{on && readOnly && hasForm && answered && (
									<span className="em-req-note">
										<ClipboardList size={13} aria-hidden="true" />
										Details
									</span>
								)}
							</label>
						);
					})}
				</div>
			)}

			<EventRequirementModal
				requirement={noting}
				icon={(noting && ICONS[noting.icon]) || FALLBACK_ICON}
				note={(noting && notes[noting.id]) || ""}
				answers={noting && answersById[noting.id]}
				onSave={saveDetails}
				onClose={() => setNoting(null)}
			/>
		</div>
	);
};
